import {
  bulkStatusModalTargets,
  MANUAL_PO_STATUS_TARGETS,
  PO_STATUS_ORDER,
  TERMINAL_PO_STATUSES,
} from "./manualStatusWorkflow";

const PO_STATUS_RANK = Object.fromEntries(PO_STATUS_ORDER.map((status, index) => [status, index]));

/**
 * Journey rank used to order extra status lanes and combined-board gates.
 *
 * @param {string} statusLabel
 * @returns {number}
 */
export function purchaseStatusRank(statusLabel) {
  return PO_STATUS_RANK[statusLabel] ?? Number.MAX_SAFE_INTEGER;
}

/** Default status board columns (manual lifecycle), always shown in order. */
export const DEFAULT_PO_STATUS_LANE_ORDER = [
  "Draft",
  "Draft Sent",
  "Created",
  ...MANUAL_PO_STATUS_TARGETS,
];

const defaultLaneSet = new Set(DEFAULT_PO_STATUS_LANE_ORDER);

/**
 * Status names on the current page that are not in the default manual lifecycle.
 *
 * @param {Array<{ status?: string }>} orders
 * @returns {string[]}
 */
export function extraPurchaseStatusLanesFromOrders(orders = []) {
  const extras = new Set();
  orders.forEach(order => {
    const status = order?.status;
    if (status && !defaultLaneSet.has(status)) {
      extras.add(status);
    }
  });
  return [...extras].sort((a, b) => {
    const rankDiff = purchaseStatusRank(a) - purchaseStatusRank(b);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    return a.localeCompare(b);
  });
}

/**
 * Column order for the Default View status board.
 *
 * @param {Array<{ status?: string }>} orders
 * @returns {string[]}
 */
export function defaultPurchaseStatusBoardColumns(orders = []) {
  return [...DEFAULT_PO_STATUS_LANE_ORDER, ...extraPurchaseStatusLanesFromOrders(orders)];
}

/**
 * Groups orders into lane keys; every column starts with an empty array.
 *
 * @param {Array<Object>} orders
 * @param {string[]} columnOrder
 * @returns {Record<string, Array<Object>>}
 */
export function groupPurchaseOrdersByStatusLanes(orders = [], columnOrder = []) {
  const groups = {};
  columnOrder.forEach(status => {
    groups[status] = [];
  });
  orders.forEach(order => {
    const status = order?.status;
    if (status && groups[status]) {
      groups[status].push(order);
    } else if (status) {
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(order);
    }
  });
  return groups;
}

/**
 * Whether a status lane accepts manual bulk-update drops.
 *
 * @param {string} laneStatus
 * @param {string} orderStatus
 * @returns {boolean}
 */
export function isPurchaseStatusDropTarget(laneStatus, orderStatus) {
  if (!laneStatus || !orderStatus || laneStatus === orderStatus) {
    return false;
  }
  if (TERMINAL_PO_STATUSES.includes(orderStatus)) {
    return false;
  }
  const allowed = bulkStatusModalTargets(orderStatus, { includeCancel: false });
  return allowed.includes(laneStatus);
}

/**
 * Whether cards in this lane can be dragged to another status lane.
 *
 * @param {string} orderStatus
 * @returns {boolean}
 */
export function isPurchaseOrderStatusDraggable(orderStatus) {
  if (!orderStatus) return false;
  if (
    TERMINAL_PO_STATUSES.includes(orderStatus) ||
    orderStatus === "Draft" ||
    orderStatus === "Draft Sent"
  ) {
    return false;
  }
  return true;
}
